import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Columns2, Layers, PawPrint, Scissors } from "lucide-react";
import Header from "@/components/Header";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { fetchSharedPreview } from "@/lib/share";

export default function SharedPreview() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing
  const [mode, setMode] = useState("slider");

  useEffect(() => {
    fetchSharedPreview(id)
      .then((d) => {
        setData(d);
        setState("ready");
      })
      .catch(() => setState("missing"));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#fdfbf7]" data-testid="shared-preview-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10 pb-24">
        {state === "loading" && (
          <div className="rounded-3xl border border-[#eae4d9] bg-white aspect-[4/3] relative overflow-hidden" data-testid="shared-loading">
            <div className="absolute inset-0 shimmer" />
          </div>
        )}

        {state === "missing" && (
          <div className="text-center py-16 fade-up" data-testid="shared-not-found">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-[#f3efe6] items-center justify-center text-[#c84125]">
              <PawPrint className="w-7 h-7" />
            </span>
            <h1 className="font-display mt-5 text-2xl sm:text-3xl font-extrabold text-[#0f172a]">This link isn't available.</h1>
            <p className="mt-2 text-sm text-[#475569]">The preview may have been removed or the link is incomplete.</p>
            <Link
              to="/studio"
              data-testid="shared-cta-studio"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-6 py-3 shadow-[0_8px_25px_-4px_rgba(255,107,74,0.35)] transition"
            >
              Create your own preview <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {state === "ready" && data && (
          <>
            <div className="text-center fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#ffe8e0] text-[#c84125] text-xs font-bold tracking-widest uppercase px-3 py-1.5">
                <Scissors className="w-3.5 h-3.5" /> Grooming request
              </span>
              <h1 data-testid="shared-title" className="font-display mt-4 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
                {data.dogName ? `${data.dogName}'s ${data.style?.name || "grooming"} look` : `${data.style?.name || "Grooming"} preview`}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-[#475569]">
                Same dog, new style. Drag to compare before and after.
              </p>
            </div>

            <div className="mt-8 fade-up delay-1">
              <div className="inline-flex rounded-full bg-white border border-[#eae4d9] p-1 shadow-sm mb-3">
                <Toggle active={mode === "slider"} onClick={() => setMode("slider")} testId="shared-mode-slider">
                  <Layers className="w-3.5 h-3.5" /> Slider
                </Toggle>
                <Toggle active={mode === "side"} onClick={() => setMode("side")} testId="shared-mode-side">
                  <Columns2 className="w-3.5 h-3.5" /> Side by side
                </Toggle>
              </div>
              {mode === "slider" ? (
                <div className="rounded-3xl overflow-hidden border-2 border-[#eae4d9] bg-white shadow-[0_18px_45px_-12px_rgba(15,23,42,0.22)]">
                  <BeforeAfterSlider beforeSrc={data.beforeImage} afterSrc={data.afterImage} afterLabel="AI PREVIEW" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Fig src={data.beforeImage} label="BEFORE" testId="shared-before-img" muted />
                  <Fig src={data.afterImage} label={`AFTER · ${(data.style?.name || "AI PREVIEW").toUpperCase()}`} testId="shared-after-img" />
                </div>
              )}
            </div>

            <div className="mt-8 rounded-3xl border border-[#eae4d9] bg-white p-6 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.06)] fade-up delay-2" data-testid="shared-details">
              <div className="text-xs font-bold tracking-widest uppercase text-[#64748b]">Grooming brief</div>
              <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Item label="Style" value={data.style?.name} />
                <Item label="Dog" value={data.dogName} />
                <Item label="Breed" value={data.breed} />
                <Item label="Body length" value={data.bodyLength} />
                <Item label="Face" value={data.faceStyle} />
                <Item label="Ears" value={data.earStyle} />
                <Item label="Tail" value={data.tailStyle} />
                <Item label="Age" value={data.age} />
              </dl>
              {data.customInstructions && (
                <div className="mt-4 rounded-2xl bg-[#fdfbf7] border border-[#eae4d9] p-4">
                  <div className="text-[10px] font-bold tracking-widest text-[#64748b]">OWNER'S NOTES</div>
                  <p className="mt-1 text-sm text-[#0f172a]">{data.customInstructions}</p>
                </div>
              )}
              <p className="mt-5 text-xs text-[#64748b] leading-relaxed">
                AI previews are visual references only. Your groomer will determine the final cut based on your dog's coat,
                condition, breed, and grooming requirements.
              </p>
            </div>

            <div className="mt-10 text-center fade-up delay-3">
              <p className="text-sm text-[#475569]">Curious what your dog would look like with a new cut?</p>
              <Link
                to="/studio"
                data-testid="shared-cta-studio"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-semibold px-6 py-3 transition"
              >
                Try GroomingPreview.ai free <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const Toggle = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
      active ? "bg-[#0f172a] text-white" : "text-[#475569] hover:text-[#0f172a]"
    }`}
  >
    {children}
  </button>
);

const Fig = ({ src, label, testId, muted }) => (
  <figure className="rounded-3xl overflow-hidden border border-[#eae4d9] bg-white shadow-[0_4px_20px_-2px_rgba(15,23,42,0.06)]">
    <div className="aspect-square bg-[#f3efe6]">
      <img src={src} alt={label} className="w-full h-full object-cover" data-testid={testId} />
    </div>
    <figcaption className={`px-4 py-3 text-[11px] font-bold tracking-[0.15em] ${muted ? "text-[#64748b]" : "text-[#c84125]"}`}>{label}</figcaption>
  </figure>
);

const Item = ({ label, value }) =>
  value ? (
    <div>
      <dt className="text-[10px] font-bold tracking-widest text-[#64748b]">{label.toUpperCase()}</dt>
      <dd className="mt-1 text-sm font-semibold text-[#0f172a]">{value}</dd>
    </div>
  ) : null;
